import type { Permission } from '@vmc/shared';
import { ShieldAlert } from 'lucide-react';
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { EmptyState } from '@/components/ui/states';
import { useAuth, useCan } from '@/lib/auth';

export function FullPageSpinner() {
  return (
    <div className="grid h-dvh place-content-center" role="status">
      <span className="size-6 animate-spin rounded-full border-2 border-primary border-r-transparent" />
      <span className="sr-only">Loading</span>
    </div>
  );
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const location = useLocation();
  if (status === 'loading') return <FullPageSpinner />;
  if (status === 'anonymous') {
    const next = location.pathname + location.search;
    return (
      <Navigate to={next === '/' ? '/login' : `/login?next=${encodeURIComponent(next)}`} replace />
    );
  }
  return children;
}

export function RequirePermission({
  permission,
  children,
}: {
  permission?: Permission;
  children: ReactNode;
}) {
  const can = useCan();
  if (permission && !can(permission)) {
    return (
      <EmptyState
        icon={<ShieldAlert />}
        title="No access"
        description="Your role does not have access to this page. Ask a super admin if you need it."
        className="py-24"
      />
    );
  }
  return children;
}
